import React, { useRef, useEffect, useState, useCallback, useContext } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import classes from "./CreatorPage.module.css";
import Bookings from "../../components/CreatorPage/Bookings";
import ProfileMenu from '../../components/NavBar/ProfileMenu';
// import { ListStudioModal } from "../../components/ListStudioModal/ListStudioModal";
import NavBarPhone from "../../components/NavBarPhone/NavBarPhone";
import CreatorNavBar from "../../components/NavBar/CreatorNavBar/CreatorNavBar";
import { isHelpMenuPanelTarget } from '../../util/isHelpMenuPanelTarget';
import ModesSwitch from "../../components/UI/ModesSwitch";
import { useAutoRefreshCreatorBookings } from '../../hooks/useAutoRefreshCreatorBookings';
import { useHasStudios } from '../../hooks/useHasStudios';
import { useBookingExpiry } from '../../hooks/useBookingExpiry';
import { useViewportHeight } from '../../hooks/useViewportHeight';
import useUnreadMessagesCount from '../../hooks/messages/useUnreadMessagesCount';
import useBookingNotifications from '../../hooks/useBookingNotifications';
import { NavBarContext } from '../../App';
// import { useFirstTimeModal } from '../../hooks/useFirstTimeModal';
// import { NavBarContext } from '../../App.js';

export const CreatorPage = () => {
    const [showProfileMenu, setShowProfileMenu] = useState(false);
    // const [activeSection, setActiveSection] = useState('bookings');
    const { user } = useSelector((state) => state?.auth);
    const { profilePicture, firstName, lastName, id: userID } = user;
    const profileMenuRef = useRef(null);
    const userContainerRef = useRef(null);

    // const { isPhoneSize } = useContext(NavBarContext);
    
    // Initialize viewport height fix for iOS Safari
    useViewportHeight();

    // Track first-time modal display
    // const { shouldShowModal, markModalAsSeen } = useFirstTimeModal(userID);

    // Fix iOS Safari scroll position issue on page load
    useEffect(() => {
        // Detect iOS Safari
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);
        const isMobileSafari = isIOS && isSafari;

        // Force scroll to top on page load to prevent Safari from maintaining previous scroll position
        const resetScrollPosition = () => {
            window.scrollTo(0, 0);
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            
            // Additional fix for mobile Safari
            if (isMobileSafari) {
                // Force viewport height recalculation
                const vh = window.innerHeight * 0.01;
                document.documentElement.style.setProperty('--vh', `${vh}px`);
                
                // Force scroll reset with multiple methods
                window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
                
                // Reset any transform that might be causing issues
                document.body.style.transform = 'translate3d(0, 0, 0)';
                document.documentElement.style.transform = 'translate3d(0, 0, 0)';
            }
        };

        // Reset immediately
        resetScrollPosition();

        // Multiple reset attempts with different timing for mobile Safari
        const timeouts = [];
        if (isMobileSafari) {
            // Reduced timeouts to allow pull-to-refresh
            timeouts.push(setTimeout(resetScrollPosition, 100));
            timeouts.push(setTimeout(resetScrollPosition, 300));
        } else {
            timeouts.push(setTimeout(resetScrollPosition, 100));
        }
        
        // Reset on page visibility change (when user returns to tab)
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                resetScrollPosition();
            }
        };

        // Reset on focus (when user returns to app)
        const handleFocus = () => {
            resetScrollPosition();
        };

        // Reset on orientation change
        const handleOrientationChange = () => {
            setTimeout(resetScrollPosition, 100);
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('orientationchange', handleOrientationChange);

        // Additional fix for mobile Safari - minimal body adjustments
        if (isMobileSafari) {
            const originalBodyStyle = {
                overflow: document.body.style.overflow,
                height: document.body.style.height,
                width: document.body.style.width
            };

            // Minimal body adjustments to allow pull-to-refresh
            document.body.style.overflow = 'auto';
            document.body.style.height = '100%';
            document.body.style.width = '100%';

            return () => {
                timeouts.forEach(clearTimeout);
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                window.removeEventListener('focus', handleFocus);
                window.removeEventListener('orientationchange', handleOrientationChange);
                
                // Restore original body styles
                document.body.style.overflow = originalBodyStyle.overflow;
                document.body.style.height = originalBodyStyle.height;
                document.body.style.width = originalBodyStyle.width;
            };
        }

        return () => {
            timeouts.forEach(clearTimeout);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, []);

    const { hasStudios } = useHasStudios(userID);
    const unreadCount = useUnreadMessagesCount();
    const { activeView } = useContext(NavBarContext);
    const bookingNotifications = useBookingNotifications(activeView);
    const hasNotifications = (unreadCount > 0) || (bookingNotifications > 0);
    
    const { bookingStudioData, loading: isBookingLoading, error: bookingError, refetch } = useAutoRefreshCreatorBookings(userID, {
        pollInterval: 30000, // Poll every 30 seconds
        enablePolling: true,
        refreshOnFocus: true,
        refreshOnVisibilityChange: true
    });


    const handleProfileMenuClick = () => {
      setShowProfileMenu(prev => !prev);
    };
  
    const handleBookingExpired = useCallback((bookingId) => {
        refetch();
    }, [refetch]);

    // Handle expiry at parent level
    useBookingExpiry(bookingStudioData?.items || [], handleBookingExpired);

    useEffect(() => {
        const handleClickOutside = (event) => {
          if (isHelpMenuPanelTarget(event.target)) {
            return;
          }

          if (
            profileMenuRef.current && 
            !profileMenuRef.current.contains(event.target) &&
            !userContainerRef.current?.contains(event.target)
          ) {
            setShowProfileMenu(false);
          }
        };
    
        document.addEventListener('mousedown', handleClickOutside);
    
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, []);  
      
    //   const activeStyle = {
    //     borderRadius: '8px',
    //     background: 'var(--light-grey-2)'
    // };

  
  return (
      <div className={classes.creatorPage}>
        <header className={classes.header}>
            <Link to="/" className={classes.logo}>
                <svg xmlns="http://www.w3.org/2000/svg" width="53" height="18" viewBox="0 0 53 18" fill="none">
                    <path d="M50.5002 9C51.8809 9 53.0002 7.88071 53.0002 6.5C53.0002 5.11929 51.8809 4 50.5002 4C49.1195 4 48.0002 5.11929 48.0002 6.5C48.0002 7.88071 49.1195 9 50.5002 9Z" fill="#FF003E"/>
                    <path d="M9.93692 9.28445L8.58239 9.17069C5.23081 8.89942 4.10204 8.5494 4.10204 7.7881C4.10204 7.0268 5.52603 6.60677 8.46083 6.60677C11.5606 6.60677 12.8109 7.01805 13.0714 8.12062L13.1148 8.29563H16.5185L16.4838 8.05062C16.1452 5.14542 13.5577 3.73658 8.58239 3.73658C3.60712 3.73658 1.05437 5.11917 1.05437 7.83185C1.05437 10.317 3.09483 11.5158 8.11352 11.9709L9.32912 12.0846C12.6546 12.3734 13.8876 12.8109 13.8876 13.686C13.8876 14.6398 12.3681 15.1298 9.36385 15.1298C5.96017 15.1298 4.2236 14.491 4.05863 13.1697L4.03258 12.9772H0.628906V13.2135C0.785197 16.3899 3.63317 18 9.10336 18C14.5736 18 16.944 16.4949 16.944 13.5285C16.944 10.8858 14.9817 9.69572 9.93692 9.28445Z" fill="#1F1F1F"/>
                    <path d="M42.1415 4.17403V9.8444C42.1415 13.0209 40.0663 14.9897 36.7321 14.9897C33.5976 14.9897 32.3733 13.5809 32.3733 9.95816V4.17403H21.8497V0L18.7152 1.33009V13.2834C18.7152 16.5823 20.2954 17.9912 24.003 17.9912C25.2968 17.9912 26.5384 17.7724 27.589 17.5537L27.7627 17.5187V14.7097L27.5022 14.7622C26.139 15.0422 25.5312 15.1473 24.5587 15.1473C22.7614 15.1473 21.8497 14.7185 21.8497 12.3646V6.7992H30.793L29.1433 8.05928V10.982C29.1433 15.7685 31.1577 17.9912 35.4731 17.9912C38.2603 17.9912 40.5525 17.0199 42.1415 15.1648V17.5537H45.3715V4.17403H42.1415Z" fill="#1F1F1F"/>
                </svg>
            </Link>
            <Link to="/" className={classes.navigationLinks}>
                <svg xmlns="http://www.w3.org/2000/svg" width="63" height="22" viewBox="0 0 63 22" fill="none">
                    <path d="M59.881 10.9998C61.5389 10.9998 62.8828 9.65582 62.8828 7.99799C62.8828 6.34015 61.5389 4.99621 59.881 4.99621C58.2232 4.99621 56.8792 6.34015 56.8792 7.99799C56.8792 9.65582 58.2232 10.9998 59.881 10.9998Z" fill="#DF315A"/>
                    <path d="M11.1762 11.3413L9.54985 11.2047C5.52557 10.879 4.17024 10.4587 4.17024 9.54462C4.17024 8.63052 5.88004 8.12619 9.40389 8.12619C13.1258 8.12619 14.6271 8.62001 14.9399 9.94389L14.992 10.154H19.0788L19.0371 9.85983C18.6305 6.37153 15.5237 4.67992 9.54985 4.67992C3.57598 4.67992 0.510854 6.34001 0.510854 9.59716C0.510854 12.5811 2.96087 14.0206 8.98687 14.5669L10.4464 14.7035C14.4395 15.0503 15.9199 15.5756 15.9199 16.6263C15.9199 17.7715 14.0954 18.3599 10.4882 18.3599C6.40132 18.3599 4.3162 17.5929 4.11811 16.0064L4.08683 15.7752H0V16.0589C0.187661 19.8729 3.60726 21.8062 10.1754 21.8062C16.7435 21.8062 19.5897 19.999 19.5897 16.4372C19.5897 13.2641 17.2335 11.8351 11.1762 11.3413Z" fill="white"/>
                    <path d="M49.8447 5.20516V12.0136C49.8447 15.8277 47.3529 18.1917 43.3495 18.1917C39.5859 18.1917 38.1159 16.5001 38.1159 12.1502V5.20516H25.48V0.193359L21.7164 1.79041V16.1429C21.7164 20.104 23.6138 21.7956 28.0656 21.7956C29.619 21.7956 31.1098 21.5329 32.3713 21.2703L32.5799 21.2282V17.8555L32.2671 17.9185C30.6303 18.2548 29.9005 18.3808 28.7328 18.3808C26.5747 18.3808 25.48 17.866 25.48 15.0396V8.35724H36.2184L34.2375 9.87024V13.3795C34.2375 19.1268 36.6563 21.7956 41.8378 21.7956C45.1844 21.7956 47.9368 20.6293 49.8447 18.4019V21.2703H53.723V5.20516H49.8447Z" fill="white"/>
                </svg>
            </Link>
            <CreatorNavBar />
            <div className={classes.headerContent}>
                <ModesSwitch />
                <div className={classes.userWrapper}>
                    {hasNotifications && (
                      <svg 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="13" 
                        height="13" 
                        viewBox="0 0 13 13" 
                        fill="none"
                        style={{
                          position: 'absolute',
                          top: '0',
                          right: '0',
                          zIndex: 10
                        }}
                      >
                        <path 
                          d="M6.5 0.75C9.67573 0.75 12.25 3.32438 12.25 6.5C12.25 9.6756 9.67571 12.25 6.5 12.25C3.32448 12.25 0.750019 9.67561 0.75 6.5C0.75 3.32436 3.32446 0.75 6.5 0.75Z" 
                          fill="var(--Stu-Rot, #DF315A)" 
                          stroke="var(--Black, #000)" 
                          strokeWidth="1.5"
                        />
                      </svg>
                    )}
                    <div 
                        ref={userContainerRef} 
                        onClick={handleProfileMenuClick} 
                        className={classes.userContainer}
                        style={{ position: 'relative' }}
                      >
                        <div className={classes.userImageContainer}>
                          <img src={profilePicture} alt="User Profile" /> 
                        </div>
                    </div>
                </div>
            </div>
        </header>
        <div className={classes.content}>
            {/* <div className={classes.navigationContainer}>
                <h2 className="titleSemibold3">Anbieter Ansicht</h2>
                <nav className={classes.navigation}>
                    <Link to="/creator">
                    <li 
                        onClick={() => setActiveSection('bookings')}
                        style={activeSection === 'bookings' ? activeStyle : {}}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="19" viewBox="0 0 18 19" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M10.8514 2.717C7.10761 1.83145 3.35258 4.15122 2.46673 7.89884C1.5813 11.6447 3.9058 15.3977 7.65154 16.2837C11.3953 17.1692 15.1504 14.8495 16.0362 11.1018L16.0365 11.1006C16.1746 10.5202 16.2354 9.93968 16.2227 9.37182L17.9223 9.33388C17.9382 10.049 17.8615 10.7744 17.6903 11.4941C16.5882 16.1538 11.9184 19.0398 7.26022 17.938C2.60361 16.8366 -0.289703 12.17 0.812325 7.50778C1.91391 2.84749 6.58385 -0.0390683 11.2422 1.06253C11.8376 1.20265 12.3995 1.40289 12.9274 1.64949L12.208 3.18976C11.7796 2.98967 11.3279 2.82912 10.8524 2.71723L10.8514 2.717Z" fill="#1F1F1F"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M16.4028 5.84862L9.18402 13.0843L4.77344 8.67372L5.97552 7.47164L9.18261 10.6787L15.1993 4.64795L16.4028 5.84862Z" fill="#1F1F1F"/>
                        </svg>
                        <span>Buchungen</span>
                    </li>
                    </Link>
                    <Link to="/calendar">
                    <li>
                          <svg xmlns="http://www.w3.org/2000/svg" width="19" height="17" viewBox="0 0 19 17" fill="none">
                              <path d="M10.0001 0.587891V0.437891H9.85006H8.92743H8.77743V0.587891V1.43846H6.0753V0.587891V0.437891H5.9253H5.00267H4.85267V0.587891V1.43846H2.25154C1.48043 1.43846 0.85 2.06567 0.85 2.84001V15.1603C0.85 15.9347 1.48043 16.5619 2.25154 16.5619H16.5259C17.297 16.5619 17.9274 15.9347 17.9274 15.1603V2.84001C17.9274 2.06867 17.2972 1.43846 16.5259 1.43846H13.9357V0.587891V0.437891H13.7857H12.8631H12.7131V0.587891V1.43846H10.0001V0.587891ZM6.0753 2.66109H8.77743V3.41711V3.56711H8.92743H9.85006H10.0001V3.41711V2.66109H12.7131V3.41711V3.56711H12.8631H13.7857H13.9357V3.41711V2.66109H16.5259C16.6242 2.66109 16.7048 2.74167 16.7048 2.84001V15.1603C16.7048 15.2587 16.6242 15.3393 16.5259 15.3393H2.25154C2.15321 15.3393 2.07263 15.2587 2.07263 15.1603V2.84001C2.07263 2.74167 2.15321 2.66109 2.25154 2.66109H4.85267V3.41711V3.56711H5.00267H5.9253H6.0753V3.41711V2.66109Z" fill="#1F1F1F" stroke="#1F1F1F" strokeWidth="0.3"/>
                              <line x1="1.80078" y1="6.13633" x2="17.7974" y2="6.13633" stroke="#1F1F1F" strokeWidth="1.7"/>
                          </svg>
                          <span>Kalender</span>
                    </li>
                    </Link>
                    <Link to="/services-studios">
                    <li>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="19" viewBox="0 0 20 19" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M5.19027 2.38945C5.13319 2.38945 5.08564 2.43656 5.08564 2.49409V4.71403H5.08281V13.0064C5.08281 13.0634 5.12992 13.111 5.18745 13.111H17.2503C17.3074 13.111 17.3549 13.0639 17.3549 13.0064V2.49409C17.3549 2.43701 17.3078 2.38945 17.2503 2.38945H5.19027ZM3.38564 2.49409C3.38564 1.50095 4.19104 0.689453 5.19027 0.689453H17.2503C18.2434 0.689453 19.0549 1.49485 19.0549 2.49409V13.0064C19.0549 13.9995 18.2495 14.811 17.2503 14.811H5.18745C4.19431 14.811 3.38281 14.0056 3.38281 13.0064V4.71403H3.38564V2.49409Z" fill="#1F1F1F"/>
                            <path fillRule="evenodd" clipRule="evenodd" d="M1.83288 5.52178C1.76004 5.52178 1.7 5.58182 1.7 5.65465V16.4776C1.7 16.5504 1.76004 16.6105 1.83288 16.6105H14.2488C14.3216 16.6105 14.3816 16.5504 14.3816 16.4776V15.972H16.0816V16.4776C16.0816 17.4893 15.2605 18.3105 14.2488 18.3105H1.83288C0.821158 18.3105 0 17.4893 0 16.4776V5.65465C0 4.64294 0.821158 3.82178 1.83288 3.82178H2.20004V5.52178H1.83288Z" fill="#1F1F1F"/>
                        </svg>
                        <span>Studios & Services</span>
                    </li>
                    </Link>
                    <Link to="/messages">
                    <li>
                        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M0.281613 9.27788C0.281613 4.52247 4.50761 0.775879 9.53227 0.775879C14.5569 0.775879 18.7829 4.52247 18.7829 9.27788C18.7829 14.0345 14.5556 17.7712 9.53227 17.7712C7.94687 17.7712 6.38217 17.3998 5.00039 16.6845L0 18.2239L1.52257 13.54C0.716284 12.2442 0.281613 10.7748 0.281613 9.27788ZM9.53227 2.47588C5.29445 2.47588 1.98161 5.60651 1.98161 9.27788C1.98161 10.4895 2.34723 11.696 3.04492 12.7658L3.05011 12.7737L3.38706 13.3033L2.62885 15.6358L5.14156 14.8622L5.64302 15.101L5.66214 15.1114C6.82171 15.7382 8.16198 16.0712 9.53227 16.0712C13.7714 16.0712 17.0829 12.9481 17.0829 9.27788C17.0829 5.60651 13.7701 2.47588 9.53227 2.47588Z" fill="#1F1F1F"/>
                        </svg>
                        <span>Nachrichten</span>
                    </li>
                    </Link>
                </nav>
            </div> */}
            <div className={classes.verticalLine}></div>
            <div className={classes.contentContainer}>
                <Bookings 
                    isLoading={isBookingLoading} 
                    bookingData={bookingStudioData?.items || []}
                    hasStudios={hasStudios}
                />
            </div>
        </div>
        <div className={classes.mobileNavBar}>
            <NavBarPhone fromBookings={true} />
        </div>
        {showProfileMenu && (
        <ProfileMenu
          ref={profileMenuRef}
          profilePicture={profilePicture}
          firstName={firstName}
          lastName={lastName}
          setShowProfileMenu={setShowProfileMenu}
        //   fromProfile={fromProfile}
          />
        )}
        {/* {!isPhoneSize && shouldShowModal && 
            <ListStudioModal initialOpen={true} onClose={markModalAsSeen} />
        } */}
      </div>
  );
};

export default CreatorPage;